import { useState } from 'react';
import { ArrowLeft, Check, Copy, Landmark, MessageCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { BANK_ACCOUNT_NUMBER, BANK_WHATSAPP_NUMBER, canCompleteTransfer } from '../../supabase/functions/_shared/paymentRules';

interface Props {
  total: number;
  submitting: boolean;
  onBack: () => void;
  onComplete: () => void;
}

export default function BankTransferScreen({ total, submitting, onBack, onComplete }: Props) {
  const [copied, setCopied] = useState(false);
  const [whatsappOpened, setWhatsappOpened] = useState(false);
  const copyAccount = async () => {
    try {
      await navigator.clipboard.writeText(BANK_ACCOUNT_NUMBER);
      setCopied(true);
    } catch {
      toast.error('Could not copy. Please select and copy the account number.');
    }
  };
  const whatsappUrl = `https://wa.me/${BANK_WHATSAPP_NUMBER}?text=${encodeURIComponent(`Hello GAMBGO, here is my bank-transfer receipt for D${total.toFixed(2)}.`)}`;

  return (
    <main className="min-h-screen bg-background text-foreground">
      <header className="border-b border-border px-4 py-4 flex items-center gap-3">
        <Button variant="ghost" size="icon" aria-label="Back to checkout" disabled={submitting} onClick={onBack}><ArrowLeft /></Button>
        <h1 className="text-xl font-semibold">Bank transfer</h1>
      </header>
      <div className="max-w-md mx-auto px-6 py-8 space-y-8">
        <div className="text-center space-y-3">
          <Landmark className="h-10 w-10 mx-auto text-primary" />
          <p className="text-sm text-muted-foreground">Amount to transfer</p>
          <h2 className="text-3xl font-semibold">D{total.toFixed(2)}</h2>
          <p className="text-sm text-muted-foreground">No processing fee</p>
        </div>
        <section className="space-y-3">
          <h3 className="font-semibold">1. Make your bank transfer</h3>
          <p className="text-sm text-muted-foreground">Transfer the exact amount to this account number.</p>
          <div className="flex items-center justify-between gap-3 bg-muted rounded-lg p-4">
            <span className="text-xl font-semibold select-all">{BANK_ACCOUNT_NUMBER}</span>
            <Button variant="ghost" size="icon" aria-label={copied ? 'Account number copied' : 'Copy account number'} title="Copy account number" onClick={copyAccount}>{copied ? <Check /> : <Copy />}</Button>
          </div>
        </section>
        <section className="space-y-3">
          <h3 className="font-semibold">2. Send your receipt</h3>
          <p className="text-sm text-muted-foreground">Take a screenshot of your transfer and send it to +2205423107 on WhatsApp.</p>
          <Button asChild variant="outline" className="w-full">
            <a href={whatsappUrl} target="_blank" rel="noopener noreferrer" onClick={() => setWhatsappOpened(true)}><MessageCircle /> Send screenshot on WhatsApp</a>
          </Button>
        </section>
        <section className="space-y-3">
          <h3 className="font-semibold">3. Complete your order</h3>
          <p className="text-sm text-muted-foreground">After sending your receipt, return here and press Completed. Your transfer will be checked before your order is confirmed.</p>
          <Button className="w-full" disabled={!canCompleteTransfer(whatsappOpened, submitting)} onClick={onComplete}><Check />{submitting ? 'Creating order…' : 'Completed'}</Button>
        </section>
      </div>
    </main>
  );
}